import ActionListItem from "@/appComponents/Blocks/Composites/ActionListItem/ActionListItem";
import { EditableText, Flex } from "@theorgchartdev/storybook";
import { appstr } from "@coreapp/utils/appstr";
import {
  GetNameValidation,
  GetNameValidationMessage,
  enumNameValidation
} from "@coreapp/utils/ValidateRename";

export const kRowSecDefintionList = {
  Equal: "$eq",
  "Not Equal": "$ne",
  In: "$in",
  "Not In": "$nin"
};

const GetTextContent = (profileName, profileID, profileType, onRename, getRenameValidation) => {
  return (
    <Flex width={"25%"}>
      <EditableText
        className="oc-fill-width"
        size={"medium"}
        bold={false}
        text={profileName}
        onChange={onRename}
        eventInfo={{ id: profileID, type: profileType }}
        validatorFn={value => getRenameValidation(value, profileType, profileID)}
        dataTestId={`${profileType}-profile-${profileID}-rename`}
      />
    </Flex>
  );
};

const GetActionContent = (key, clickHandler, profileType, hasAdvSec) => {
  const dataTestId = `${profileType}-profile-${key}`;
  if (key === "Default Security" && !hasAdvSec) {
    return [
      {
        iconName: "gear",
        dataTestId: `${dataTestId}-edit`,
        onClick: () => clickHandler("edit", profileType, key)
      },
      {
        iconName: "copy",
        dataTestId: `${dataTestId}-copy`,
        onClick: () => clickHandler("copy", profileType, key)
      }
    ];
  } else if (key != "Default Security" && !hasAdvSec) {
    return [
      {
        iconName: "gear",
        dataTestId: `${dataTestId}-edit`,
        onClick: () => clickHandler("edit", profileType, key)
      },
      {
        iconName: "copy",
        dataTestId: `${dataTestId}-copy`,
        onClick: () => clickHandler("copy", profileType, key)
      },
      {
        iconName: "trash-can",
        iconProps: { color: "error" },
        dataTestId: `${dataTestId}-delete`,
        onClick: () => clickHandler("delete", profileType, key)
      }
    ];
  } else return [];
};

export const BuildSecurityList = (
  securityObj,
  clickHandler,
  profileType,
  onRename,
  hasAdvSec,
  getRenameValidation
) => {
  let options = [];
  for (const key in securityObj) {
    const dataTestId = `${profileType}-profile-${key}`;
    if (key === "Default Security") {
      const defaultContent = {
        content: (
          <ActionListItem
            dataTestId={dataTestId}
            padding="large"
            contentBefore={GetTextContent(
              securityObj?.name ? securityObj.name : key,
              key,
              profileType,
              onRename,
              getRenameValidation
            )}
            actions={GetActionContent(key, clickHandler, profileType, hasAdvSec)}
          />
        ),
        padding: false,
        divider: true,
        value: key
      };
      options.push(defaultContent);
    } else {
      const securityContent = {
        content: (
          <ActionListItem
            dataTestId={dataTestId}
            padding="large"
            contentBefore={GetTextContent(
              securityObj[key]?.name || appstr("Untitled"),
              key,
              profileType,
              onRename,
              getRenameValidation
            )}
            actions={GetActionContent(key, clickHandler, profileType, hasAdvSec)}
          />
        ),
        padding: false,
        divider: true,
        value: key
      };
      options.push(securityContent);
    }
  }
  return options;
};

const ValidateNameAndID = (profiles, newID, newName) => {
  let existingIDs = [];
  let existingNames = [];

  for (const profileID in profiles) {
    existingIDs.push(profileID);
    let profile = profiles[profileID];
    if (profile.name) existingNames.push(profile.name);
    else existingNames.push(profileID);
  }

  while (existingIDs.indexOf(newID) > -1) newID = (parseInt(newID) + 1).toString();

  let counter = 1;
  let validName = newName;

  while (existingNames.indexOf(validName) > -1) {
    validName = validName + " (" + counter.toString() + ")";
    counter++;
  }

  return { newID, validName };
};

export const HandleCopyProfile = (profiles, profileID) => {
  let newProfile = { ...profiles[profileID] };
  let testID = "0";
  let testName = newProfile?.name ? `${newProfile.name} ${appstr("Copy")}` : appstr("Copy");

  const { newID, validName } = ValidateNameAndID(profiles, testID, testName);

  newProfile.name = validName;
  profiles[newID] = newProfile;

  return profiles;
};

export const GetRulePropertyString = theProperty => {
  let aboutString;
  let propertyString;
  switch (theProperty) {
    case "$ne":
      propertyString = appstr("Not Equal");
      aboutString = appstr("neqdesc");
      break;
    case "$in":
      propertyString = appstr("In");
      aboutString = appstr("indesc");
      break;
    case "$nin":
      propertyString = appstr("Not In");
      aboutString = appstr("nindesc");
      break;
    case "$eq":
    default:
      propertyString = appstr("Equal");
      aboutString = appstr("eqdesc");
      break;
  }
  return { aboutString, propertyString };
};

export const getProfileNameError = (securityConfig, name, profileType, existingProfileID) => {
  const trimmed = (name ?? "").trim();
  const reason = GetNameValidation(trimmed);
  if (reason !== enumNameValidation.valid) return GetNameValidationMessage(reason);

  const target = trimmed.toLowerCase();
  // RLS "0" and BLS "0" are distinct profiles, so exclude the edited one by bucket and id.
  const isDuplicate = ["rowlevel", "branchlevel"].some(bucket =>
    Object.entries(securityConfig?.[bucket] ?? {}).some(
      ([id, profile]) =>
        !(bucket === profileType && id === existingProfileID) &&
        (profile.name ?? "").trim().toLowerCase() === target
    )
  );
  return isDuplicate ? appstr("nameinuse") : "";
};

export const SetRowSecProfileToJSON = (accountConfig, updatedProfile, profileID) => {
  var rowLevelConfig = accountConfig["security"]["rowlevel"]
    ? JSON.parse(JSON.stringify(accountConfig["security"]["rowlevel"]))
    : {};
  var updatedSecurityProfile = {};

  updatedSecurityProfile["name"] = updatedProfile.name;
  updatedSecurityProfile["description"] = updatedProfile.description || "";

  if (!updatedProfile["conditionalFields"]) delete updatedSecurityProfile["conditionalFields"];
  else updatedSecurityProfile["conditionalFields"] = updatedProfile["conditionalFields"];

  if (!updatedProfile["allowedFields"]) delete updatedSecurityProfile["allowedFields"];
  else updatedSecurityProfile["allowedFields"] = updatedProfile["allowedFields"];

  if (!updatedProfile["restrictedFields"]) delete updatedSecurityProfile["restrictedFields"];
  else updatedSecurityProfile["restrictedFields"] = updatedProfile["restrictedFields"];

  if (!updatedProfile["pos_allowedFields"]) delete updatedSecurityProfile["pos_allowedFields"];
  else updatedSecurityProfile["pos_allowedFields"] = updatedProfile["pos_allowedFields"];

  if (!updatedProfile["pos_restrictedFields"])
    delete updatedSecurityProfile["pos_restrictedFields"];
  else updatedSecurityProfile["pos_restrictedFields"] = updatedProfile["pos_restrictedFields"];

  var newSecurityObj = JSON.parse(JSON.stringify(updatedSecurityProfile));
  rowLevelConfig[profileID] = newSecurityObj;

  return rowLevelConfig;
};

export const SetBranchSecProfileToJSON = (accountConfig, updatedProfile, profileID) => {
  var branchLevelConfig = accountConfig["security"]["branchlevel"]
    ? JSON.parse(JSON.stringify(accountConfig["security"]["branchlevel"]))
    : {};
  branchLevelConfig[profileID] = JSON.parse(JSON.stringify(updatedProfile));
  return branchLevelConfig;
};
